import type { FC, ReactNode } from 'react'
import type { ContentCard } from '@/lib/content/types'

import Link from 'next/link'
import { ArrowUpRight, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cmsKey } from '@/lib/portal/cms'
import { formatDate } from '@/lib/format'
import { CoverArt } from '@/components/site/cover-art'
import { contentHref, contentTable, field, isCaseStudyCard, summaryOf, TopicChip } from '@/components/site/article-meta'
import { useLocale } from '@/i18n/use-locale'
import { common } from '@/i18n/common'
import { siteContent } from '@/i18n/site-content'

interface ArticleCardProps {
    item: ContentCard
    featured?: boolean
    as?: 'h2' | 'h3'
}

export const ArticleCard: FC<ArticleCardProps> = ({ item, featured = false, as = 'h3' }): ReactNode => {
    const locale = useLocale()
    const copy = siteContent[locale]
    const table = contentTable(item.type)
    const href = contentHref(item.type, item.slug)
    const title = field(item, 'title', locale)
    const summary = summaryOf(item, locale)
    const kind = item.type === 'insight' && item.kind ? field(item, 'kind', locale) : null
    const study = isCaseStudyCard(item) ? item : null
    const industry = study?.industry ? field(study, 'industry', locale) : null
    const client = study?.client_label ? field(study, 'client_label', locale) : null
    const location = study?.location ? field(study, 'location', locale) : null
    const Heading = as

    return (
        <article className='h-full'>
            <Link
                href={href}
                className={cn(
                    'group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card transition-[border-color,box-shadow,transform] duration-300 hover:border-primary/25 hover:shadow-[0_28px_60px_-34px_rgba(13,21,38,0.45)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring motion-safe:hover:-translate-y-0.5',
                    featured && 'lg:grid lg:grid-cols-[1.15fr_1fr]'
                )}
            >
                <div className={cn('relative overflow-hidden bg-mist', featured ? 'aspect-[16/10] lg:aspect-auto lg:min-h-[440px]' : 'aspect-[16/10]')}>
                    {item.cover ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            src={item.cover}
                            alt={item.cover_alt ?? ''}
                            className='absolute inset-0 size-full object-cover transition-transform duration-700 motion-safe:group-hover:scale-[1.03]'
                            loading={featured ? 'eager' : 'lazy'}
                            decoding='async'
                        />
                    ) : (
                        <CoverArt
                            seed={item.slug}
                            label={copy.types[item.type]}
                            className='absolute inset-0 size-full transition-transform duration-700 motion-safe:group-hover:scale-[1.03]'
                        />
                    )}
                </div>

                <div className={cn('flex flex-1 flex-col', featured ? 'p-7 sm:p-10 lg:p-12' : 'p-6 sm:p-7')}>
                    <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
                        {featured && (
                            <span className='inline-flex items-center rounded-full bg-ink px-3 py-1 text-xs font-medium text-white'>
                                {copy.listing.featured}
                            </span>
                        )}

                        <TopicChip item={item} locale={locale} />

                        {industry && (
                            <span
                                className='inline-flex items-center rounded-full border border-primary/15 bg-accent px-3 py-1 text-xs font-medium text-accent-foreground'
                                data-mdlfy-cms={cmsKey(table, item.id, industry.column)}
                            >
                                {industry.value}
                            </span>
                        )}

                        {kind && (
                            <span className='text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground' data-mdlfy-cms={cmsKey(table, item.id, kind.column)}>
                                {kind.value}
                            </span>
                        )}

                        {client && (
                            <span className='text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground' data-mdlfy-cms={cmsKey(table, item.id, client.column)}>
                                {client.value}
                            </span>
                        )}
                    </div>

                    <Heading
                        className={cn(
                            'mt-5 font-display tracking-[-0.01em] text-foreground transition-colors group-hover:text-primary',
                            featured ? 'text-[2.1rem] leading-[1.06] sm:text-[2.6rem] lg:text-[2.9rem]' : 'text-[1.65rem] leading-[1.12]'
                        )}
                    >
                        <span data-mdlfy-cms={cmsKey(table, item.id, title.column)}>
                            {title.value}
                        </span>
                    </Heading>

                    {summary.value && (
                        <p
                            className={cn('mt-4 leading-relaxed text-muted-foreground', featured ? 'line-clamp-4 text-[1.05rem]' : 'line-clamp-3 text-[0.95rem]')}
                            data-mdlfy-cms={cmsKey(table, item.id, summary.column)}
                        >
                            {summary.value}
                        </p>
                    )}

                    <div className='mt-auto flex items-center justify-between gap-4 pt-7 text-sm text-muted-foreground'>
                        <div className='flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1'>
                            {location && (
                                <span className='inline-flex min-w-0 items-center gap-1.5'>
                                    <MapPin className='size-4 shrink-0' aria-hidden='true' />
                                    <span className='line-clamp-1' data-mdlfy-cms={cmsKey(table, item.id, location.column)}>
                                        {location.value}
                                    </span>
                                </span>
                            )}

                            {!location && item.published_at && (
                                <time dateTime={item.published_at} suppressHydrationWarning>
                                    {formatDate(item.published_at, locale, 'long')}
                                </time>
                            )}

                            {!location && item.published_at && item.read_minutes && (
                                <span aria-hidden='true'>
                                    ·
                                </span>
                            )}

                            {item.read_minutes && (
                                <span>
                                    <span data-mdlfy-cms={cmsKey(table, item.id, 'read_minutes')}>{item.read_minutes}</span>
                                    {` ${common[locale].minRead}`}
                                </span>
                            )}
                        </div>

                        <span className='flex size-9 shrink-0 items-center justify-center rounded-full border border-border text-foreground transition-colors group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground' aria-hidden='true'>
                            <ArrowUpRight className='size-4' />
                        </span>
                    </div>
                </div>
            </Link>
        </article>
    )
}
